import { useEffect, useState } from "react";
import { api, formatCOP } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Archive, Upload, Sparkles, TrendingUp, Percent, Blend, X, Info } from "lucide-react";
import ImportMappingWizard from "@/components/ImportMappingWizard";
import UsageTip from "@/components/UsageTip";
import { toast } from "sonner";

/**
 * LegacyInventoryPage
 * Página exclusiva del director para manejar mercancía que ya está en bodega
 * pero llegó sin registro en el ERP (sin TRM, sin FOB conocido).
 *
 * Flujo:
 *   1. Ver lista de productos ya marcados como legacy
 *   2. Botón "Importar Excel legacy" abre el ImportMappingWizard →
 *      luego pide estrategia (TRM / margen / promedio) → llama /legacy/preview → /legacy/confirmar
 */
export default function LegacyInventoryPage() {
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [strategyModal, setStrategyModal] = useState(null); // {mappedRows, proveedor}
  const [previewData, setPreviewData] = useState(null); // resultado de /legacy/preview
  const [strategy, setStrategy] = useState("margen");
  const [margenPct, setMargenPct] = useState(40);
  const [trmHist, setTrmHist] = useState(4100);
  const [notas, setNotas] = useState("");
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/importar/legacy/list");
      setItems(data);
    } catch (e) {
      toast.error("No se pudo cargar la lista");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isDirector) load();
  }, [isDirector]);

  if (!isDirector) {
    return (
      <div className="p-8 text-center">
        <Archive className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
        <p>Solo directores pueden gestionar inventario legacy.</p>
      </div>
    );
  }

  const handleWizardDone = async ({ file, proveedor, mapping }) => {
    // Leer TODAS las filas remapeadas del archivo original con el mapping confirmado
    try {
      const cleanMap = {};
      Object.entries(mapping).forEach(([c, i]) => {
        if (i !== undefined && i !== null && i !== "") cleanMap[c] = Number(i);
      });
      const form = new FormData();
      form.append("file", file);
      form.append("mapping_json", JSON.stringify(cleanMap));
      const { data } = await api.post("/importar/remapear", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.rows?.length) {
        toast.error("No se detectaron filas de datos");
        return;
      }
      setStrategyModal({ mappedRows: data.rows, proveedor });
      setPreviewData(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo procesar el archivo");
    }
  };

  const runPreview = async () => {
    if (!strategyModal) return;
    try {
      const { data } = await api.post("/importar/legacy/preview", {
        rows: strategyModal.mappedRows,
        strategy,
        margen_objetivo_pct: strategy === "margen" ? Number(margenPct) : undefined,
        trm_historica: strategy === "trm_historica" ? Number(trmHist) : undefined,
        proveedor: strategyModal.proveedor,
        notas: notas || undefined,
      });
      setPreviewData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo hacer preview");
    }
  };

  const confirmarImport = async () => {
    if (!strategyModal || !previewData) return;
    setImporting(true);
    try {
      const { data } = await api.post("/importar/legacy/confirmar", {
        rows: strategyModal.mappedRows,
        strategy,
        margen_objetivo_pct: strategy === "margen" ? Number(margenPct) : undefined,
        trm_historica: strategy === "trm_historica" ? Number(trmHist) : undefined,
        proveedor: strategyModal.proveedor,
        notas: notas || undefined,
      });
      toast.success(`Import legacy listo: ${data.creados} creados, ${data.actualizados} actualizados. ${data.errores.length ? data.errores.length + ' con errores' : ''}`);
      setStrategyModal(null);
      setPreviewData(null);
      setStrategy("margen");
      setNotas("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo importar");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="legacy-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Catálogo</p>
          <h1 className="text-4xl font-serif tracking-tight">Inventario legacy</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Mercancía que ya está en tu bodega pero llegó sin registro completo (sin TRM, sin FOB del día). Elige una estrategia
            para estimar su costo y consolidarlo con futuros contenedores.
          </p>
        </div>
        <Button onClick={() => setWizardOpen(true)} data-testid="legacy-import-btn">
          <Upload className="h-4 w-4 mr-2" /> Importar Excel legacy
        </Button>
      </div>

      <UsageTip
        id="legacy-inventario-v1"
        title="¿Qué es esto?"
        tips={[
          "<b>Legacy</b> = mercancía que llegó antes de que usaras Beem, o en un contenedor que no registraste.",
          "Sabes cuánto tienes y el precio, pero no la <b>TRM</b> ni el <b>FOB</b> exactos del día que la pagaste.",
          "Elige una estrategia: <b>TRM histórica</b> (metes TRM promedio del año), <b>Margen</b> (retro-ingeniería desde precio mínimo) o <b>Promedio</b> (esperar al próximo contenedor para promediar).",
          "Cuando llegue un contenedor con el mismo SKU y estrategia=promedio, el sistema calcula automáticamente el costo ponderado por unidades y quita el flag legacy.",
        ]}
      />

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Productos marcados como legacy ({items.length})</h2>
          {loading && <span className="text-xs text-muted-foreground">Cargando...</span>}
        </div>
        {items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Archive className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Sin productos legacy aún.</p>
            <p className="text-xs mt-1">Importa un Excel con la mercancía que llegó sin registro, o marca productos existentes desde la página de Productos.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="beem-table w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="p-2">Producto</th>
                  <th className="p-2">SKU</th>
                  <th className="p-2 text-right">Stock</th>
                  <th className="p-2 text-right">Costo estimado</th>
                  <th className="p-2">Estrategia</th>
                  <th className="p-2 text-right">Precio mín / sug</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b hover:bg-muted/30" data-testid={`legacy-row-${p.id}`}>
                    <td className="p-2 font-medium">{p.nombre}</td>
                    <td className="p-2 text-xs text-muted-foreground">{p.sku || "—"}</td>
                    <td className="p-2 text-right">{p.stock_actual || 0}</td>
                    <td className="p-2 text-right">{p.costo ? formatCOP(p.costo) : <span className="text-amber-600">Pendiente</span>}</td>
                    <td className="p-2">
                      <StrategyBadge strategy={p.legacy_meta?.strategy} />
                      {p.legacy_meta?.notas && (
                        <p className="text-[10px] text-muted-foreground mt-0.5" title={p.legacy_meta.notas}>
                          {p.legacy_meta.notas.slice(0, 40)}{p.legacy_meta.notas.length > 40 ? "…" : ""}
                        </p>
                      )}
                    </td>
                    <td className="p-2 text-right text-xs">
                      {formatCOP(p.precio_minimo)} / {formatCOP(p.precio_sugerido)}
                    </td>
                    <td className="p-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          if (!window.confirm(`Desmarcar "${p.nombre}" como legacy? Su costo quedará fijo con el valor actual.`)) return;
                          try {
                            await api.post(`/importar/legacy/desmarcar/${p.id}`);
                            toast.success("Desmarcado");
                            load();
                          } catch (e) {
                            toast.error("No se pudo desmarcar");
                          }
                        }}
                        data-testid={`legacy-unmark-${p.id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ImportMappingWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        title="Importar inventario legacy"
        requiredFields={["sku", "nombre", "precio_minimo"]}
        onDone={handleWizardDone}
      />

      {/* Strategy Modal */}
      {strategyModal && (
        <Dialog open onOpenChange={() => setStrategyModal(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Elegir estrategia de costo para {strategyModal.mappedRows.length} producto(s)</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-[75vh] overflow-y-auto">
              <div className="grid md:grid-cols-3 gap-2">
                <StrategyOption
                  active={strategy === "margen"}
                  icon={Percent}
                  title="Margen objetivo"
                  desc="Retro-ingeniería desde precio mínimo. Asume tu margen histórico."
                  onClick={() => setStrategy("margen")}
                />
                <StrategyOption
                  active={strategy === "trm_historica"}
                  icon={TrendingUp}
                  title="TRM histórica"
                  desc="Usar una TRM promedio del año que pagaste."
                  onClick={() => setStrategy("trm_historica")}
                />
                <StrategyOption
                  active={strategy === "promedio"}
                  icon={Blend}
                  title="Promedio ponderado"
                  desc="Esperar al próximo contenedor y promediar por unidades."
                  onClick={() => setStrategy("promedio")}
                />
              </div>

              {strategy === "margen" && (
                <div>
                  <Label>Margen objetivo (%)</Label>
                  <Input
                    type="number"
                    value={margenPct}
                    onChange={(e) => setMargenPct(e.target.value)}
                    data-testid="strategy-margen-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Fórmula: costo = precio_mínimo ÷ (1 + margen/100). Con {margenPct}% y precio_min = $14.000 → costo ≈ ${formatCOP(Math.round(14000 / (1 + Number(margenPct || 0)/100)))}.
                  </p>
                </div>
              )}
              {strategy === "trm_historica" && (
                <div>
                  <Label>TRM histórica (COP por USD)</Label>
                  <Input
                    type="number"
                    value={trmHist}
                    onChange={(e) => setTrmHist(e.target.value)}
                    data-testid="strategy-trm-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Estima FOB desde el precio mínimo asumiendo factor conservador (×1.8). Costo ≈ precio_min ÷ 1.8. La TRM queda anotada en la metadata.
                  </p>
                </div>
              )}
              {strategy === "promedio" && (
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm">
                  <p className="flex items-center gap-2 font-semibold text-blue-900">
                    <Info className="h-4 w-4" /> Costo pendiente
                  </p>
                  <p className="text-xs text-blue-800 mt-1">
                    Los productos quedarán marcados con <strong>costo = 0</strong> temporal. Cuando llegue el próximo contenedor con el mismo SKU y le aceptes el cambio de precio, el sistema
                    calcula automáticamente:
                    <br />
                    <code className="text-[11px]">costo_final = (stock_viejo × 0 + cantidad_nueva × costo_nuevo) / total</code>
                    <br />
                    Y quita el flag legacy.
                  </p>
                </div>
              )}

              <div>
                <Label>Notas (opcional)</Label>
                <Input
                  placeholder="ej: Contenedor 2025 sin registro, mercancía Anclad"
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                />
              </div>

              <div className="flex justify-between gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setStrategyModal(null)}>Cancelar</Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={runPreview} data-testid="strategy-preview-btn">
                    Ver preview
                  </Button>
                  <Button onClick={confirmarImport} disabled={!previewData || importing} data-testid="strategy-confirm-btn">
                    {importing ? "Importando..." : `Importar ${strategyModal.mappedRows.length} producto(s)`}
                  </Button>
                </div>
              </div>

              {previewData && (
                <div className="rounded-lg border overflow-hidden">
                  <div className="p-2 bg-muted text-xs flex gap-4">
                    <span>✓ A crear: <strong>{previewData.resumen.a_crear}</strong></span>
                    <span>✓ A actualizar: <strong>{previewData.resumen.a_actualizar}</strong></span>
                    <span className="text-rose-600">✕ Con errores: <strong>{previewData.resumen.con_errores}</strong></span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-2 text-left">Acción</th>
                          <th className="p-2 text-left">SKU</th>
                          <th className="p-2 text-left">Nombre</th>
                          <th className="p-2 text-right">Cant.</th>
                          <th className="p-2 text-right">Precio mín</th>
                          <th className="p-2 text-right">Costo calc</th>
                          <th className="p-2 text-left">Errores</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.rows.map((r) => (
                          <tr key={r.idx} className={`border-t ${r.errores.length ? "bg-rose-50" : ""}`}>
                            <td className="p-2">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                r.accion === "crear" ? "bg-emerald-100 text-emerald-800" :
                                r.accion === "actualizar" ? "bg-blue-100 text-blue-800" :
                                "bg-gray-100 text-gray-600"
                              }`}>{r.accion}</span>
                            </td>
                            <td className="p-2">{r.sku || "—"}</td>
                            <td className="p-2">{r.nombre}</td>
                            <td className="p-2 text-right">{r.cantidad}</td>
                            <td className="p-2 text-right">{formatCOP(r.precio_minimo)}</td>
                            <td className="p-2 text-right">{formatCOP(r.costo_calculado)}</td>
                            <td className="p-2 text-rose-600">{r.errores.join(", ")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function StrategyBadge({ strategy }) {
  if (!strategy) return <span className="text-xs text-muted-foreground">—</span>;
  const map = {
    trm_historica: { label: "TRM histórica", class: "bg-purple-100 text-purple-800" },
    margen: { label: "Margen retro", class: "bg-amber-100 text-amber-800" },
    promedio: { label: "Promedio pendiente", class: "bg-blue-100 text-blue-800" },
  };
  const s = map[strategy] || { label: strategy, class: "bg-gray-100 text-gray-800" };
  return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${s.class}`}>{s.label}</span>;
}

function StrategyOption({ active, icon: Icon, title, desc, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`text-left p-3 rounded-lg border-2 transition-all ${
        active ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-primary/40"
      }`}
      data-testid={`strategy-opt-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <Icon className={`h-5 w-5 mb-1 ${active ? "text-primary" : "text-muted-foreground"}`} />
      <p className="font-semibold text-sm">{title}</p>
      <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{desc}</p>
    </button>
  );
}
